import { ART_PAL, fade, mix, n, rng, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Golden Flower scene behind the game zone: an evening lacquer room — lattice windows (回纹 key pattern) on both sides
 * that fade out towards the middle, a warm light pool over the table and, on the wide scene only, two plum branches (梅花)
 * in the free bands beside the table: one reaching in from the left edge below the title row, one rising from the
 * bottom right towards the rail. Nothing runs under the panels (title row, parts, disclaimer, the right rail): no
 * pillars, no floor line (owner, 2026-09-30). The phone has no free band beside the table, so its scene is the lattice
 * and the light only. The centre stays calm. No filters, no text.
 * Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%).
 */
export function zhajinhuaScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sz${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;

  const top = P.dark ? mix(P.bg, P.red, 0.14) : mix(P.gold, '#FFFFFF', 0.86);
  const mid = P.dark ? mix(P.bg, P.red, 0.09) : mix(P.red, '#FFFFFF', 0.92);
  const bottom = P.dark ? mix(P.bg, P.red, 0.05) : mix(P.red, '#FFFFFF', 0.88);
  const gold = warm(P);
  const line = P.dark ? gold : P.red;
  const branch = P.dark ? mix('#5A3A2A', P.bg, 0.3) : '#8A5A44';
  const petal = P.dark ? mix(P.pink, P.bg, 0.25) : tint(P.pink, 0.35);
  const petal2 = P.dark ? mix('#FFFFFF', P.pink, 0.45) : '#FFFFFF';
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  // lattice: a 40 × 40 key-pattern tile, faded towards the middle by a mask
  const T = wide ? 40 : 28;
  const k = T / 40;
  const tile =
    `<pattern id="${p}lt" width="${T}" height="${T}" patternUnits="userSpaceOnUse">` +
    `<path d="M${n(2 * k)} ${n(2 * k)}H${n(38 * k)}V${n(38 * k)}H${n(2 * k)}ZM${n(10 * k)} ${n(10 * k)}H${n(30 * k)}V${n(30 * k)}H${n(16 * k)}V${n(16 * k)}H${n(24 * k)}V${n(24 * k)}" fill="none" stroke="${line}" stroke-width="${n(1.4 * k + 0.3)}"/></pattern>`;
  const band = wide ? 330 : 64;
  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, bottom)}${tile}` +
    `<linearGradient id="${p}fl" x1="0" x2="1"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<linearGradient id="${p}fr" x1="1" x2="0"><stop offset="0" stop-color="#FFF" stop-opacity=".9"/><stop offset="1" stop-color="#FFF" stop-opacity="0"/></linearGradient>` +
    `<mask id="${p}ml"><rect width="${band}" height="${H}" fill="url(#${p}fl)"/></mask>` +
    `<mask id="${p}mr"><rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}fr)"/></mask>` +
    fade(`${p}pool`, gold, P.dark ? 0.14 : 0.22) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.35 : 0.45) +
    `</defs>`;

  /** a plum blossom: five round petals, a gold heart */
  const blossom = (x: number, y: number, r: number, c: string) => {
    let s = '';
    for (let i = 0; i < 5; i++) {
      const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
      s += `<circle cx="${n(x + Math.cos(a) * r * 0.62)}" cy="${n(y + Math.sin(a) * r * 0.62)}" r="${n(r * 0.5)}"/>`;
    }
    return `<g fill="${c}">${s}</g><circle cx="${n(x)}" cy="${n(y)}" r="${n(r * 0.26)}" fill="${gold}"/>`;
  };
  /**
   * a plum branch: a few bent strokes and blossoms/buds along them (deterministic), from (x, y) to the right (flip -1:
   * to the left), turned by `rot` degrees around its start
   */
  const plum = (x: number, y: number, s: number, flip: 1 | -1, seed: number, rot = 0) => {
    const r = rng(seed);
    const X = (dx: number) => n(x + dx * s * flip);
    const Y = (dy: number) => n(y + dy * s);
    // the motif mark: the middle of the branch after the turn
    const a = (rot * Math.PI) / 180;
    const mx = 160 * s * flip;
    const my = 50 * s;
    const motif = Math.round(x + mx * Math.cos(a) - my * Math.sin(a));
    const turn = rot ? ` transform="rotate(${rot} ${n(x)} ${n(y)})"` : '';
    const main = `M${X(0)} ${Y(0)}C${X(60)} ${Y(30)} ${X(110)} ${Y(20)} ${X(180)} ${Y(70)}S${X(270)} ${Y(110)} ${X(330)} ${Y(96)}`;
    const twig1 = `M${X(120)} ${Y(34)}C${X(140)} ${Y(-6)} ${X(170)} ${Y(-20)} ${X(210)} ${Y(-24)}`;
    const twig2 = `M${X(230)} ${Y(92)}C${X(236)} ${Y(130)} ${X(256)} ${Y(150)} ${X(284)} ${Y(162)}`;
    let flowers = '';
    const spots: [number, number][] = [
      [70, 36],
      [150, 30],
      [190, -18],
      [212, -26],
      [250, 106],
      [284, 160],
      [320, 98],
      [104, 24],
    ];
    spots.forEach(([dx, dy], i) => {
      const rr = (i % 3 === 2 ? 5 : 9 + r() * 4) * s;
      flowers += blossom(x + dx * s * flip, y + dy * s, rr, i % 2 ? petal : petal2);
    });
    return (
      `<g data-motif="${motif}"${turn}><g fill="none" stroke="${branch}" stroke-linecap="round" stroke-opacity="${o(0.9, 0.75)}">` +
      `<path d="${main}" stroke-width="${n(7 * s)}"/><path d="${twig1}" stroke-width="${n(4 * s)}"/><path d="${twig2}" stroke-width="${n(3.5 * s)}"/></g>` +
      `<g fill-opacity="${o(0.75, 0.9)}">${flowers}</g></g>`
    );
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<g opacity="${o(0.1, 0.12)}"><rect width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}ml)"/>` +
    `<rect x="${W - band}" width="${band}" height="${H}" fill="url(#${p}lt)" mask="url(#${p}mr)"/></g>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.12)}" rx="${n(W * 0.36)}" ry="${n(H * 0.5)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.5)}" rx="${n(W * 0.34)}" ry="${n(H * 0.4)}" fill="url(#${p}calm)"/>`;

  // the wide scene is cropped to the game zone (cover): at 1440 × 900 about x 210…1390 of it is seen, the table covers
  // x ≈ 440…1150, the title row sits over the top left, the parts and the disclaimer over the bottom left
  if (wide)
    art +=
      // left: in from the page edge, between the title row and the parts, clear of the table
      plum(100, 300, 0.95, 1, 7) +
      // right: up from the bottom edge towards the rail, turned so its twigs point away from the table
      plum(1310, H, 0.95, 1, 11, -115);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Golden Flower: a gold key pattern (回纹) on a thin line (repeat-x tile, 16 × 8). */
export function zhajinhuaAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const gold = warm(P);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 7.5H16M2.5 7.5V1.5H12.5V5.5H6.5V3.5H9.5" fill="none" stroke="${gold}" stroke-opacity="${P.dark ? 0.7 : 0.85}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'zhajinhua', scene: zhajinhuaScene, accent: zhajinhuaAccent });
